import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Drawer, Select, message } from 'antd';
import { PlusCircleOutlined, UserOutlined, DownOutlined, RightOutlined, FilePdfOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import MemberSelector from '../components/MemberSelector';
import StudentArchive from '../components/StudentArchive';
import SingleMemberScheduler from '../components/SingleMemberScheduler';
import StageEvaluationComponent from '../components/StageEvaluationComponent';
import SportsTestComponent from '../components/SportsTestComponent';
import useTrainingPhases from '../hooks/useTrainingPhases';

const TrainingPlan = ({ user }) => {
  // 会员选择相关状态
  const [selectedMember, setSelectedMember] = useState(() => {
    // 从localStorage恢复之前选择的会员信息
    try {
      const savedMember = localStorage.getItem('trainingPlan_selectedMember');
      return savedMember ? JSON.parse(savedMember) : null;
    } catch (error) {
      console.warn('恢复会员选择状态失败:', error);
      return null;
    }
  });
  const [memberDrawerVisible, setMemberDrawerVisible] = useState(false);
  
  // 各模块展开状态（默认收起）
  const [archiveExpanded, setArchiveExpanded] = useState(false);
  const [schedulerExpanded, setSchedulerExpanded] = useState(false);
  const [evaluationExpanded, setEvaluationExpanded] = useState(false);
  const [sportsTestExpanded, setSportsTestExpanded] = useState(false);
  
  // 使用自定义hook获取训练阶段信息
  const { totalPhases, loading: phasesLoading, error: phasesError, fetchTrainingPhases } = useTrainingPhases(selectedMember?.id);
  
  // 阶段选择状态
  const [currentPhase, setCurrentPhase] = useState(1);
  
  // 标记是否是初次加载
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // 记录上一次的会员ID，用于检测会员切换
  const [previousMemberId, setPreviousMemberId] = useState(null);
  
  // 总开关状态
  const [allExpanded, setAllExpanded] = useState(false);
  
  // PDF导出相关状态
  const [exporting, setExporting] = useState(false);
  const trainingPlanRef = useRef(null);

  // 选择会员回调
  const handleMemberSelect = (member) => {
    setSelectedMember(member);
    // 保存到localStorage
    try {
      localStorage.setItem('trainingPlan_selectedMember', JSON.stringify(member));
    } catch (error) {
      console.warn('保存会员选择状态失败:', error);
    }
    setMemberDrawerVisible(false);
  };

  // 获取当前会员ID
  const getSelectedMemberId = () => {
    return selectedMember ? [selectedMember.id] : [];
  };

  // 处理会员信息变更
  const handleMemberInfoChange = (newInfo) => {
    const updatedMember = {...selectedMember, ...newInfo};
    setSelectedMember(updatedMember);
    // 更新localStorage中的会员信息
    try {
      localStorage.setItem('trainingPlan_selectedMember', JSON.stringify(updatedMember));
    } catch (error) {
      console.warn('更新会员信息状态失败:', error);
    }
  };

  // 处理阶段变化
  const handlePhaseChange = (phase) => {
    setCurrentPhase(phase);
  };

  // 处理排课数据加载完成
  const handleScheduleDataLoad = (data) => {
    // 这里可以添加额外的处理逻辑
    console.log('排课数据加载完成:', data);
  };

  // 当选中会员变化时，重新获取阶段信息并设置默认选中最新阶段
  useEffect(() => {
    const isMemberChanged = selectedMember?.id !== previousMemberId;
    
    if (selectedMember?.id) {
      fetchTrainingPhases().then(result => {
        if (result && result.totalPhases > 0) {
          // 如果当前选择的阶段超出了新会员的最大阶段数，则调整到最大可用阶段
          if (currentPhase > result.totalPhases) {
            setCurrentPhase(result.totalPhases);
          }
          // 以下情况自动设置为最新阶段：
          // 1. 当前是默认阶段1
          // 2. 初次加载
          // 3. 会员发生切换（无论阶段数变多还是变少）
          else if (currentPhase === 1 || isInitialLoad || isMemberChanged) {
            setCurrentPhase(result.totalPhases);
            setIsInitialLoad(false);
          }
        }
      });
      
      // 更新上一次的会员ID
      setPreviousMemberId(selectedMember.id);
    } else {
      // 没有选中会员时重置状态
      setCurrentPhase(1);
      setIsInitialLoad(true);
      setPreviousMemberId(null);
    }
  }, [selectedMember?.id, fetchTrainingPhases, currentPhase, isInitialLoad, previousMemberId]);

  // 监听页面卸载事件，在页面关闭时清除状态
  useEffect(() => {
    const handleBeforeUnload = () => {
      // 可以在这里添加清理逻辑，但保留会员选择状态
      // 因为我们希望在用户主动关闭浏览器标签页时才清除状态
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // 返回清理函数
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // 展开/收起控制函数（同时保存到localStorage）
  const toggleArchive = () => {
    const newState = !archiveExpanded;
    setArchiveExpanded(newState);
    try {
      localStorage.setItem('trainingPlan_archiveExpanded', JSON.stringify(newState));
    } catch (error) {
      console.warn('保存档案展开状态失败:', error);
    }
  };
  
  const toggleScheduler = () => {
    const newState = !schedulerExpanded;
    setSchedulerExpanded(newState);
    try {
      localStorage.setItem('trainingPlan_schedulerExpanded', JSON.stringify(newState));
    } catch (error) {
      console.warn('保存排课展开状态失败:', error);
    }
  };
  
  const toggleEvaluation = () => {
    const newState = !evaluationExpanded;
    setEvaluationExpanded(newState);
    try {
      localStorage.setItem('trainingPlan_evaluationExpanded', JSON.stringify(newState));
    } catch (error) {
      console.warn('保存测评展开状态失败:', error);
    }
  };
  
  const toggleSportsTest = () => {
    const newState = !sportsTestExpanded;
    setSportsTestExpanded(newState);
    try {
      localStorage.setItem('trainingPlan_sportsTestExpanded', JSON.stringify(newState));
    } catch (error) {
      console.warn('保存测试展开状态失败:', error);
    }
  };
  
  // 总开关控制函数（同时保存到localStorage）
  const handleToggleAll = () => {
    const newState = !allExpanded;
    setAllExpanded(newState);
    setSchedulerExpanded(newState);
    setEvaluationExpanded(newState);
    setSportsTestExpanded(newState);
    
    // 保存所有展开状态
    try {
      localStorage.setItem('trainingPlan_schedulerExpanded', JSON.stringify(newState));
      localStorage.setItem('trainingPlan_evaluationExpanded', JSON.stringify(newState));
      localStorage.setItem('trainingPlan_sportsTestExpanded', JSON.stringify(newState));
    } catch (error) {
      console.warn('保存总开关状态失败:', error);
    }
  };

  // PDF导出函数
  const handleExportPDF = async () => {
    if (!trainingPlanRef.current) {
      message.error('无法导出PDF：页面未正确初始化');
      return;
    }

    setExporting(true);
    try {
      // 等待DOM更新完成
      await new Promise((resolve) => requestAnimationFrame(resolve));
      
      // 使用html2canvas截图整个训练教案区域
      const canvas = await html2canvas(trainingPlanRef.current, {
        scale: 2,
        useCORS: true,
        scrollY: -window.scrollY,
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // 计算图片尺寸以适应A4纸张
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const printableWidth = pageWidth - margin * 2;
      const printableHeight = pageHeight - margin * 2;
      const imgHeight = (canvas.height * printableWidth) / canvas.width;
      let heightLeft = imgHeight - printableHeight;

      // 添加第一页
      pdf.addImage(imgData, 'PNG', margin, margin, printableWidth, imgHeight);

      // 如果内容超出一页，添加更多页面
      while (heightLeft > 0) {
        const position = margin - (imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, printableWidth, imgHeight);
        heightLeft -= printableHeight;
      }

      // 生成文件名：训练教案_姓名_第N训练阶段_当前时间戳
      const timestamp = new Date().getTime();
      const fileName = selectedMember 
        ? `训练教案_${selectedMember.babyName}_第${currentPhase}训练阶段_${timestamp}`
        : `训练教案_未选择会员_第${currentPhase}训练阶段_${timestamp}`;

      pdf.save(`${fileName}.pdf`);
      message.success('训练教案已导出为PDF');
    } catch (error) {
      console.error('导出PDF失败:', error);
      message.error('导出PDF失败，请稍后重试');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="menu-item-content">
      <Card 
        title="训练教案" 
        style={{ margin: '16px' }}
        extra={
          <div style={{ display: 'flex', gap: '8px' }}>
            {selectedMember && (
              <Button 
                onClick={handleExportPDF}
                type="default"
                size="middle"
                icon={<FilePdfOutlined />}
                loading={exporting}
              >
                导出PDF
              </Button>
            )}
            {selectedMember && (
              <Button 
                onClick={() => {
                  try {
                    localStorage.removeItem('trainingPlan_selectedMember');
                    localStorage.removeItem('trainingPlan_archiveExpanded');
                    localStorage.removeItem('trainingPlan_schedulerExpanded');
                    localStorage.removeItem('trainingPlan_evaluationExpanded');
                    localStorage.removeItem('trainingPlan_sportsTestExpanded');
                    setSelectedMember(null);
                    setArchiveExpanded(false);
                    setSchedulerExpanded(false);
                    setEvaluationExpanded(false);
                    setSportsTestExpanded(false);
                    setAllExpanded(false);
                  } catch (error) {
                    console.warn('清除状态失败:', error);
                  }
                }}
                type="default"
                size="middle"
              >
                初始化页面
              </Button>
            )}
            <Button 
              type="primary" 
              size="middle"
              onClick={() => setMemberDrawerVisible(true)}
              icon={<PlusCircleOutlined />}
            >
              选择会员
            </Button>
          </div>
        }
      >
        {selectedMember ? (
          <div ref={trainingPlanRef}>
            {/* 会员信息展示区域 */}
            <div style={{ 
              backgroundColor: '#f0f2f5', 
              padding: '12px 16px', 
              borderRadius: '4px', 
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <UserOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
              <div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  当前会员: {selectedMember.babyName}
                </div>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  会员ID: {selectedMember.id} | 
                  性别: {selectedMember.babySex === '1' ? '男' : '女'} | 
                  生日: {selectedMember.babyBirthday}
                </div>
              </div>
            </div>

            {/* 纵向模块布局 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* 会员档案模块 */}
              <Card 
                title={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>📋 会员档案</span>
                    <Button 
                      type="text" 
                      icon={archiveExpanded ? <DownOutlined /> : <RightOutlined />} 
                      onClick={toggleArchive}
                      size="small"
                    >
                      {archiveExpanded ? '收起' : '展开'}
                    </Button>
                  </div>
                }
                style={{ borderRadius: '6px' }}
              >
                {archiveExpanded && (
                  <StudentArchive 
                    memberId={selectedMember.id}
                    memberInfo={selectedMember}
                    onSaveSuccess={() => {
                      // 档案保存成功后的回调
                      console.log('档案保存成功');
                    }}
                    readonly={false}
                  />
                )}
              </Card>

              {/* 阶段选择器 */}
              <div style={{ 
                backgroundColor: '#f0f2f5', 
                padding: '12px 16px', 
                borderRadius: '4px', 
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <span style={{ fontWeight: 'bold' }}>训练阶段选择:</span>
                <Select
                  value={currentPhase}
                  onChange={handlePhaseChange}
                  style={{ width: '180px' }}
                  loading={phasesLoading}
                  disabled={!selectedMember || totalPhases <= 0}
                  options={Array.from({ length: totalPhases }, (_, i) => ({
                    label: `第 ${i + 1} 阶段`,
                    value: i + 1,
                  }))}
                />
                <span>
                  {phasesLoading ? '加载中...' : `共 ${totalPhases} 个训练阶段`}
                </span>
                {phasesError && (
                  <span style={{ color: '#ff4d4f', fontSize: '12px' }}>
                    获取阶段信息失败
                  </span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                  <Button 
                    type={allExpanded ? 'default' : 'primary'}
                    size="small"
                    onClick={handleToggleAll}
                  >
                    {allExpanded ? '全部收起' : '全部展开'}
                  </Button>
                </div>
              </div>

              {/* 训练计划模块 */}
              <Card 
                title={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>📅 训练计划</span>
                    <Button 
                      type="text" 
                      icon={schedulerExpanded ? <DownOutlined /> : <RightOutlined />} 
                      onClick={toggleScheduler}
                      size="small"
                    >
                      {schedulerExpanded ? '收起' : '展开'}
                    </Button>
                  </div>
                }
                style={{ borderRadius: '6px' }}
              >
                {schedulerExpanded && (
                  <SingleMemberScheduler 
                    memberId={selectedMember.id}
                    memberInfo={selectedMember}
                    onMemberChange={handleMemberInfoChange}
                    onSaveSuccess={() => {
                      // 排课保存成功后的回调
                      console.log('排课保存成功');
                    }}
                    phase={currentPhase}
                  />
                )}
              </Card>

              {/* 阶段测评模块 */}
              <Card 
                title={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>📊 阶段测评</span>
                    <Button 
                      type="text" 
                      icon={evaluationExpanded ? <DownOutlined /> : <RightOutlined />} 
                      onClick={toggleEvaluation}
                      size="small"
                    >
                      {evaluationExpanded ? '收起' : '展开'}
                    </Button>
                  </div>
                }
                style={{ borderRadius: '6px' }}
              >
                {evaluationExpanded && (
                  <StageEvaluationComponent 
                    memberId={selectedMember.id}
                    memberInfo={selectedMember}
                    onMemberChange={handleMemberInfoChange}
                    phase={currentPhase}
                  />
                )}
              </Card>

              {/* 体育测试模块 */}
              <Card 
                title={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>🏃 体育测试</span>
                    <Button 
                      type="text" 
                      icon={sportsTestExpanded ? <DownOutlined /> : <RightOutlined />} 
                      onClick={toggleSportsTest}
                      size="small"
                    >
                      {sportsTestExpanded ? '收起' : '展开'}
                    </Button>
                  </div>
                }
                style={{ borderRadius: '6px' }}
              >
                {sportsTestExpanded && (
                  <SportsTestComponent 
                    memberId={selectedMember.id}
                    memberInfo={selectedMember}
                    onMemberChange={handleMemberInfoChange}
                    phase={currentPhase}
                  />
                )}
              </Card>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '18px', color: '#666', marginBottom: '16px' }}>
              <UserOutlined style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
              <div>请先选择会员</div>
            </div>
            <div style={{ color: '#999', fontSize: '14px' }}>
              点击右上角"选择会员"按钮开始训练教案管理
            </div>
          </div>
        )}
      </Card>

      {/* 会员选择抽屉 */}
      <Drawer
        title="选择会员"
        placement="right"
        width={480}
        onClose={() => setMemberDrawerVisible(false)}
        open={memberDrawerVisible}
      >
        <MemberSelector
          onSelect={handleMemberSelect}
          selectedMemberIds={getSelectedMemberId()}
        />
      </Drawer>
    </div>
  );
};

export default TrainingPlan;